import { RendererUnavailableError } from "../../app/createSlotApp";
import { LaunchRefusedError } from "../session/LaunchRefusedError";
import { failureModal, genericFailure } from "../../system-modal/failureCopy";
import type { SystemModalConfig, SystemModalOutcome } from "../../system-modal/SystemModalTypes";
import { BOOT_FAILURE_TAG, BootFailureElement, defineBootFailureElement, type BootFailureKind } from "./BootFailureElement";
import type { Log } from "@slot/presentation";

/** The system modal as the boundary drives it once the chrome exists. */
export interface BoundaryModal {
  show(config: SystemModalConfig): Promise<SystemModalOutcome>;
}

type Translate = (key: string, fallback: string) => string;

/**
 * The one place an unexpected failure ends up (user, 2026-09-13): the boot promise's rejection,
 * the window's error event and its unhandled-rejection event all land in `fail`. Before the chrome
 * is built it shows the boot-failure popup on the host; once `attachSystemModal` has been called it
 * shows the system modal with the dictionary's copy. Either way the only offer is a reload, and
 * the player is told once: later failures are logged, not shown again. A refused launch is the
 * exception (decision 81): its words show with no button, since a reload opens the same link.
 *
 * This is the one global besides the dev hook, and it lives in boot where globals are allowed.
 */
export class BootBoundary {
  private readonly element: BootFailureElement;
  private readonly log: Log;
  private readonly reload: () => void;
  private systemModal: { modal: BoundaryModal; t: Translate } | null = null;
  private dismissLoader: (() => void) | null = null;
  private failed = false;

  public constructor(input: { host: HTMLElement; log: Log; reload: () => void }) {
    this.log = input.log;
    this.reload = input.reload;
    defineBootFailureElement();
    this.element = document.createElement(BOOT_FAILURE_TAG) as BootFailureElement;
    this.element.bind({ onReload: () => this.reload() });
    input.host.appendChild(this.element);
    window.addEventListener("error", (event) => this.fail(event.error ?? event.message));
    window.addEventListener("unhandledrejection", (event) => this.fail(event.reason));
  }

  /** From here on a failure shows in the system modal, in the dictionary's words. */
  public attachSystemModal(modal: BoundaryModal, t: Translate): void {
    this.systemModal = { modal, t };
  }

  /**
   * The boot loader, so a failure can take it off the screen first. It is a full-screen element in
   * `index.html` mounted after the host, so it paints OVER the failure popup — a boot that dies
   * while it is up would otherwise leave the player on a loader that never finishes and never
   * explains itself (decision 19).
   */
  public attachBootLoader(dismiss: () => void): void {
    this.dismissLoader = dismiss;
  }

  /** Reports a failure: logged every time, shown once. */
  public fail(error: unknown): void {
    const kind: BootFailureKind = error instanceof RendererUnavailableError ? "renderer" : "generic";
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    this.log(`[BootBoundary] ${kind} failure${this.failed ? " (already shown)" : ""}: ${message}`);
    if (this.failed) return;
    this.failed = true;
    this.dismissLoader?.();
    // A refused launch (decision 81): a reload would open the same link, so its words show with no button.
    if (error instanceof LaunchRefusedError) {
      this.element.showFinal(error.copy);
      return;
    }
    if (this.systemModal) {
      const { modal, t } = this.systemModal;
      void modal.show(failureModal(genericFailure(), t)).then(() => this.reload());
      return;
    }
    this.element.show(kind);
  }

  /** Whether a failure has been shown (the dev hook and tests). */
  public hasFailed(): boolean {
    return this.failed;
  }
}
